/**
 * Extracts a user-friendly error message from an Axios error, 
 * including blob responses (e.g., file download errors), 
 * normal JSON responses from the backend, or generic JS errors.
 *
 * @async
 * @param {any} error - The caught error (AxiosError, JS Error, or any object)
 * @param {string} [fallbackMessage='Something went wrong'] - Default message if no specific message is found
 * @returns {Promise<string>} The extracted error message
 *
 * @example
 * // Blob error (file download endpoint)
 * try {
 *   await ExportIntegration(payload);
 * } catch (error) {
 *   const message = await extractErrorMessage(error, 'Export failed');
 *   danger(message);
 * }
 *
 * @example
 * // Normal JSON error from Axios
 * try {
 *   await axios.post('/api/some-endpoint', payload);
 * } catch (error) {
 *   const message = await extractErrorMessage(error, 'Request failed');
 *   danger(message);
 * }
 *
 * @example
 * // Generic JS error
 * try {
 *   throw new Error('Unexpected error occurred');
 * } catch (error) {
 *   const message = await extractErrorMessage(error, 'Operation failed');
 *   danger(message);
 * }
 */
export const extractErrorMessage = async (
  error,
  fallbackMessage = 'Something went wrong'
) => {
  let errorMessage = fallbackMessage;

  const data = error?.response?.data;

  // Blob response (e.g., file download endpoint)
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const json = JSON.parse(text);
      errorMessage = json?.message || fallbackMessage;
    } catch {
      // Blob wasn't JSON (likely HTML or empty)
      errorMessage = fallbackMessage;
    }

  // Regular JSON response from backend
  } else if (data?.message) {
    errorMessage = data.message;

  } else if (typeof data?.error === 'string' && data.error) {
    errorMessage = data.error;

  // Generic JS error
  } else if (error?.message) {
    errorMessage = error.message;
  }

   // Axios error but no response received (timeout, network failure)
  if (error?.request && !error?.response) {
    return 'Network error: The server did not respond. Please check your connection and try again.';
  }

  return errorMessage;
};
